import { useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { SelectInput } from 'components/inputs/SelectInput';
import { useGetBlueprintDetails } from 'features/blueprints';
import { useGetBlueprintPowerProfile } from 'features/blueprints/hooks/useGetBlueprintPowerProfile';
import { useGetEquipmentRecommendations } from 'features/blueprints/hooks/useGetEquipmentRecommendations';
import { CurrencyNumberInput, Icons, PowerProfileChart, powerProfileModes } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { useGetRentalBatteries } from 'hooks/useEquipment';
import { useGetAvailableEquipment } from 'hooks/useEquipment/useGetAvailableEquipment';
import { PowerProfileStatsDisplay } from 'pages/Blueprints/components/PowerProfileStatsDisplay';
import { calculateManualWeeklyStats } from 'pages/Blueprints/utils';
import { getProjectDurationWeeks } from 'pages/Blueprints/utils/getProjectDurationWeeks';
import { isGreenerEmployee } from 'utils/authorization';
import { EQUIPMENT_TYPES } from 'utils/constants';
import { BlueprintFormData } from '../types';
import {
  MIN_RECOMMENDED_GENSET_KVA,
  RentalSource,
  currencyOptions,
  gensetCountOptions,
  gensetKvaOptions,
  hybridGensetKvaOptions,
} from '../utils/constants';
import {
  calculateBatteryCount,
  findBatteryWith422,
  findOptimalGenset,
  findOptimalManualGensetOption,
  getAutocompleteValue,
  getEquipmentOptions,
  getHybridGensetKvaOption,
  getOptionLabel,
  handleAutocompleteChange,
  isOptionEqualToValue,
} from '../utils/equipmentAutocompleteUtils';
import { convertCsvToPowerProfileData } from '../utils/powerProfileCsvConverter';
import { aggregateDataByMinute, getValidationError } from '../utils/powerRequirementsUtils';
import { daysToIndices } from '../utils/sortDays';
import { BlueprintStepSection } from './BlueprintStepSection';
import { ConventionalGensetValidationAlert } from './ConventionalGensetValidationAlert';
import { EquipmentImage } from './EquipmentImage';
import { EquipmentValidationAlerts } from './EquipmentValidationAlerts';
import { ResponsiveFormContainer } from './ResponsiveFormContainer';

interface BusinessCaseStepProps {
  formMethods: UseFormReturn<BlueprintFormData>;
  blueprintId?: number;
  mode?: 'create' | 'edit';
}

export const BusinessCaseStep = ({ formMethods, mode = 'create', blueprintId }: BusinessCaseStepProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    control,
    formState: { errors },
  } = formMethods;

  const { equipment: allEquipment, isLoading: isEquipmentLoading } = useGetAvailableEquipment();
  const { equipment: rentalBatteries, isLoading: isRentalBatteriesLoading } = useGetRentalBatteries();

  // For batteries, use rental batteries from Greener catalog, for gensets use organization equipment
  const batteryEquipment = rentalBatteries;
  const gensetEquipment = allEquipment;

  // Fetch power profile CSV in edit mode
  const { powerProfileData } = useGetBlueprintPowerProfile({
    blueprintId: blueprintId?.toString() || '',
    enabled: mode === 'edit' && !!blueprintId,
  });

  // Get power profile and grid size
  const editedPowerProfileCsv = formMethods.watch('editedPowerProfileCsv');
  const rawPowerProfileCsv = formMethods.watch('rawPowerProfileCsv');
  const mainsConnection = formMethods.watch('mainsConnection');
  const mainsConnectionCapacityUnit = formMethods.watch('mainsConnectionCapacityUnit');
  const maxAmpsAvailable = formMethods.watch('maxAmpsAvailable');
  const maxKwAvailable = formMethods.watch('maxKwAvailable');
  const peakPower = formMethods.watch('peakPowerKw');
  const averagePowerKw = formMethods.watch('averagePowerKw');
  const peaksPerDay = formMethods.watch('peaksPerDay');
  const peaksDuration = formMethods.watch('peaksDuration');
  const restDayAveragePowerKw = formMethods.watch('restDayAveragePowerKw');
  const selectedWorkingDays = formMethods.watch('workingDays');
  const workingHoursStart = formMethods.watch('workingHoursStart');
  const workingHoursEnd = formMethods.watch('workingHoursEnd');
  const startDate = formMethods.watch('startDate');
  const endDate = formMethods.watch('endDate');
  const powerProfileMode = formMethods.watch('powerProfileMode');
  const projectDurationWeeks = getProjectDurationWeeks({ startDate, endDate });

  // Use editedPowerProfileCsv if available, otherwise fallback to rawPowerProfileCsv, then powerProfileData from API
  const powerProfileCsv = editedPowerProfileCsv || rawPowerProfileCsv;

  // Convert to chart data - use API data if CSV not available yet
  const chartData = useMemo(
    () => (powerProfileCsv ? convertCsvToPowerProfileData(powerProfileCsv) : powerProfileData),
    [powerProfileCsv, powerProfileData]
  );

  const chartData1minInterval = useMemo(() => (chartData ? aggregateDataByMinute(chartData) : undefined), [chartData]);
  const hasManualPowerProfileRequiredFields =
    Array.isArray(selectedWorkingDays) &&
    peakPower != null &&
    averagePowerKw != null &&
    !!workingHoursStart &&
    !!workingHoursEnd;

  const manualPowerProfileData = useMemo(() => {
    if (powerProfileMode !== powerProfileModes.manual || !hasManualPowerProfileRequiredFields) {
      return undefined;
    }
    return {
      active_days: daysToIndices(selectedWorkingDays),
      active_period_peak_power_kw: peakPower,
      active_period_average_power_kw: averagePowerKw,
      active_period_start_time: workingHoursStart,
      active_period_end_time: workingHoursEnd,
      peaks_per_active_period: peaksPerDay ?? 0,
      peaks_duration_seconds: peaksDuration ?? 0,
      idle_period_average_power_kw: restDayAveragePowerKw ?? 0,
    };
  }, [
    powerProfileMode,
    selectedWorkingDays,
    peakPower,
    averagePowerKw,
    workingHoursStart,
    workingHoursEnd,
    peaksPerDay,
    peaksDuration,
    restDayAveragePowerKw,
    hasManualPowerProfileRequiredFields,
  ]);

  const manualWeeklyStats = useMemo(() => {
    return calculateManualWeeklyStats({
      peakPowerKw: peakPower,
      averagePowerKw,
      peaksPerDay,
      peaksDurationSeconds: peaksDuration,
      restDayAveragePowerKw,
      workingDaysCount: Array.isArray(selectedWorkingDays) ? selectedWorkingDays.length : 0,
      workingHoursStart,
      workingHoursEnd,
    });
  }, [
    peakPower,
    averagePowerKw,
    peaksPerDay,
    peaksDuration,
    restDayAveragePowerKw,
    selectedWorkingDays,
    workingHoursStart,
    workingHoursEnd,
  ]);

  // Calculate grid size: if mainsConnection is false, send 0, otherwise use maxAmpsAvailable (or 0 if not set)
  const gridSizeAmperes = mainsConnection && mainsConnectionCapacityUnit === 'amps' ? maxAmpsAvailable || 0 : 0;
  const gridSizeKw = mainsConnection && mainsConnectionCapacityUnit === 'kw' ? maxKwAvailable || 0 : 0;
  const kitePowerScenario = formMethods.watch('kitePowerScenario');

  const { data: recommendations } = useGetEquipmentRecommendations({
    gridSizeAmperes,
    gridSizeKw,
    powerProfile: powerProfileCsv,
    blueprintId,
    mode,
    powerProfileMode,
    manualPowerProfileData,
    kitePowerScenario,
  });

  const { blueprintDetails } = useGetBlueprintDetails({
    blueprintId: blueprintId?.toString() || '',
    enabled: mode === 'edit' && !!blueprintId,
  });

  // Get rental sources (catalog vs manual)
  const gensetRentalSource = formMethods.watch('gensetRentalSource');
  const batteryRentalSource = formMethods.watch('batteryRentalSource');

  // Track if we've already auto-selected equipment to avoid overwriting user's manual selection
  const hasAutoSelectedBattery = useRef(false);
  const hasAutoSelectedGenset = useRef(false);

  // Track if user has manually selected genset rental source
  const hasManuallySelectedGensetSource = useRef(false);

  // Track if manual genset was suggested
  const hasSuggestedGensetOption = useRef(false);

  const defaultValues = formMethods.formState.defaultValues;

  const hasRecommendationInputsChanged =
    (defaultValues?.mainsConnection ?? false) !== mainsConnection ||
    (defaultValues?.mainsConnectionCapacityUnit ?? 'amps') !== mainsConnectionCapacityUnit ||
    (defaultValues?.maxAmpsAvailable ?? undefined) !== maxAmpsAvailable ||
    (defaultValues?.maxKwAvailable ?? undefined) !== maxKwAvailable ||
    (defaultValues?.editedPowerProfileCsv ?? '') !== (editedPowerProfileCsv ?? '') ||
    (defaultValues?.rawPowerProfileCsv ?? '') !== (rawPowerProfileCsv ?? '');

  // Auto-select battery with "422" in name and calculate count based on requirements
  useEffect(() => {
    const shouldAutoSelect =
      batteryRentalSource === RentalSource.CATALOG &&
      !isRentalBatteriesLoading &&
      batteryEquipment.length > 0 &&
      !hasAutoSelectedBattery.current;

    if (!shouldAutoSelect) {
      return;
    }

    const currentBatteryId = formMethods.getValues('batteryModel');
    if (currentBatteryId && currentBatteryId !== '') {
      hasAutoSelectedBattery.current = true;
      return;
    }

    const battery422 = findBatteryWith422(batteryEquipment);
    if (!battery422?.kwhCapacity) {
      return;
    }

    const count = calculateBatteryCount(battery422, recommendations?.batteryMinKwh);
    formMethods.setValue('batteryModel', battery422.equipmentId.toString(), {
      shouldValidate: false,
    });
    formMethods.setValue('batteryRentalCount', count, { shouldValidate: false });
    hasAutoSelectedBattery.current = true;
  }, [batteryRentalSource, isRentalBatteriesLoading, batteryEquipment, recommendations, formMethods]);

  const hybridGensetKva = formMethods.watch('hybridGensetKva');

  const isGreenerUser = useMemo(() => isGreenerEmployee(user), [user]);
  const hasAvailableEquipment = !isEquipmentLoading && gensetEquipment.length > 0;
  const isGensetRequired = recommendations?.gensetMinKva !== 0;
  const isGensetBelowMinRecommended = hybridGensetKva && Number(hybridGensetKva) < MIN_RECOMMENDED_GENSET_KVA;

  //In create mode preselect manual mode for greener employees
  useEffect(() => {
    const shouldPreselectManualMode = mode === 'create' && isGreenerUser;

    if (!shouldPreselectManualMode) {
      return;
    }

    hasSuggestedGensetOption.current = true;

    formMethods.setValue('gensetRentalSource', RentalSource.MANUAL, { shouldValidate: true });
  }, [isGreenerUser, mode]);

  //If no genset equipment available preselect manual mode
  useEffect(() => {
    const shouldPreselectManualGenset =
      !hasAvailableEquipment && isGensetRequired && formMethods.getValues('gensetRentalSource') !== RentalSource.MANUAL;

    if (!shouldPreselectManualGenset) {
      return;
    }

    formMethods.setValue('gensetRentalSource', RentalSource.MANUAL, { shouldValidate: true });
    formMethods.setValue('gensetType', '', { shouldValidate: false });
    formMethods.setValue('gensetRentalCount', 1, { shouldValidate: false });
  }, [isGensetRequired, hasAvailableEquipment, formMethods, isGreenerUser]);

  //When manual mode is preselected find optimal kva option and count
  useEffect(() => {
    const shouldSetSuggestedManualGensetKva =
      gensetRentalSource === RentalSource.MANUAL &&
      isGensetRequired &&
      (!hasAvailableEquipment || hasSuggestedGensetOption.current);

    if (!shouldSetSuggestedManualGensetKva) {
      return;
    }
    const suggestedManualGenset = findOptimalManualGensetOption(recommendations?.gensetMinKva);
    const suggestedKvaOption =
      suggestedManualGenset?.kvaOption ?? getHybridGensetKvaOption(recommendations?.gensetMinKva);

    formMethods.setValue('hybridGensetKva', suggestedKvaOption, { shouldValidate: true });
    if (suggestedManualGenset) {
      formMethods.setValue('gensetRentalCount', suggestedManualGenset.count, { shouldValidate: true });
    }
  }, [gensetRentalSource, isGensetRequired, hasAvailableEquipment, formMethods, isGreenerUser]);

  // Auto-select smallest genset that meets requirements and calculate count
  useEffect(() => {
    const shouldAutoSelect =
      gensetRentalSource === RentalSource.CATALOG && hasAvailableEquipment && !hasAutoSelectedGenset.current;

    if (!shouldAutoSelect) {
      return;
    }

    const currentGensetId = formMethods.getValues('gensetType');
    if (currentGensetId && currentGensetId !== '') {
      hasAutoSelectedGenset.current = true;
      return;
    }

    const optimalGenset = findOptimalGenset(gensetEquipment, recommendations?.gensetMinKva);
    if (!optimalGenset) {
      return;
    }

    formMethods.setValue('gensetType', optimalGenset.genset.equipmentId.toString(), {
      shouldValidate: false,
    });
    formMethods.setValue('gensetRentalCount', optimalGenset.count, { shouldValidate: false });
    hasAutoSelectedGenset.current = true;
  }, [gensetRentalSource, hasAvailableEquipment, recommendations, formMethods, isGreenerUser]);

  useEffect(() => {
    // Select no genset for hybrid setup if recommended gensetMinKva === 0 after recommendation inputs changed, or when editing previously selected no genset value
    const shouldPreselectNoGensetFromRecommendations =
      !isGensetRequired &&
      gensetRentalSource !== RentalSource.NONE &&
      !hasManuallySelectedGensetSource.current &&
      hasRecommendationInputsChanged;

    const shouldPreselectNoGensetFromSavedValue = formMethods.watch('gensetRentalCount') === 0;

    if (!shouldPreselectNoGensetFromRecommendations && !shouldPreselectNoGensetFromSavedValue) {
      return;
    }
    formMethods.setValue('gensetRentalSource', RentalSource.NONE, { shouldValidate: false });
  }, [isGensetRequired, gensetRentalSource, formMethods, hasRecommendationInputsChanged]);

  // Get rental counts (number of units)
  const gensetRentalCount = formMethods.watch('gensetRentalCount') || 1;
  const batteryRentalCount = formMethods.watch('batteryRentalCount') || 1;

  // Get selected equipment from catalog (Step 3 catalog mode)
  const selectedGensetId = formMethods.watch('gensetType');
  const selectedBatteryId = formMethods.watch('batteryModel');
  const selectedGensetEquipment =
    gensetRentalSource === RentalSource.CATALOG
      ? gensetEquipment.find((eq) => eq.equipmentId.toString() === selectedGensetId?.toString())
      : null;
  const selectedBatteryEquipment =
    batteryRentalSource === RentalSource.CATALOG
      ? batteryEquipment.find((eq) => eq.equipmentId.toString() === selectedBatteryId?.toString())
      : null;

  // Get manual values (Step 3 manual mode)
  const gensetKvaManual = gensetRentalSource === RentalSource.MANUAL ? formMethods.watch('hybridGensetKva') : undefined;
  const batteryKwhManual =
    batteryRentalSource === RentalSource.MANUAL
      ? (() => {
          const value = formMethods.watch('batterySize');
          if (value === undefined || value === '') {
            return undefined;
          }
          const num = Number(value);
          return isNaN(num) ? undefined : num;
        })()
      : undefined;

  const batteryKwManual =
    batteryRentalSource === RentalSource.MANUAL
      ? (() => {
          const value = formMethods.watch('converterPower');
          if (value === undefined || value === '') {
            return undefined;
          }
          const num = Number(value);
          return isNaN(num) ? undefined : num;
        })()
      : undefined;

  const gensetValidationFormData = useMemo(
    () => ({
      powerProfileMode: formMethods.watch('powerProfileMode'),
      peakPowerKw: formMethods.watch('peakPowerKw'),
      editedPowerProfileCsv,
      rawPowerProfileCsv,
      gensetKva: formMethods.watch('gensetKva'),
      gensetCount: formMethods.watch('gensetCount'),
    }),
    [
      formMethods.watch('powerProfileMode'),
      formMethods.watch('peakPowerKw'),
      editedPowerProfileCsv,
      rawPowerProfileCsv,
      formMethods.watch('gensetKva'),
      formMethods.watch('gensetCount'),
    ]
  );

  const backendLoadProfile = blueprintDetails?.calculations?.[0]?.loadProfile;
  const energyRequiredWeeklyStats = manualWeeklyStats?.energyRequiredWeekly ?? backendLoadProfile?.weeklyLoadKwh;
  const averagePowerStats = manualWeeklyStats?.averagePowerWeekly ?? backendLoadProfile?.avgLoadKw;
  const peakPowerStats = peakPower ?? backendLoadProfile?.peakLoadKw;

  return (
    <>
      <BlueprintStepSection title={t('blueprints:wizard.fields.priceAssumptions.title')} titleSpacing={20}>
        <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={200} gap={3}>
          <SelectInput
            data-testid="currency-selector"
            control={control}
            name="currency"
            label={t('blueprints:wizard.fields.currency.label')}
            options={currencyOptions}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? t(currencyOptions.find((opt) => opt.value === option)?.label || '')
                : t(option.label)
            }
            getOptionValue={(option) => option.value}
            addNoneOption={false}
          />

          <CurrencyNumberInput
            data-testid="conventional-diesel-price-input"
            label={t('blueprints:wizard.fields.priceAssumptions.fuelPrice')}
            value={formMethods.watch('conventionalDieselPricePerLiter')}
            onChange={(value) => {
              formMethods.setValue('conventionalDieselPricePerLiter', value, { shouldValidate: true });
              formMethods.setValue('hybridDieselPricePerLiter', value, { shouldValidate: true });
            }}
            currency={formMethods.watch('currency')}
            error={!!errors.conventionalDieselPricePerLiter}
            helperText={
              errors.conventionalDieselPricePerLiter ? t(errors.conventionalDieselPricePerLiter.message || '') : ''
            }
            required
            placeholder="0.00"
          />

          <CurrencyNumberInput
            label={t('blueprints:wizard.fields.priceAssumptionsHybrid.electricityPricePerKwh')}
            value={formMethods.watch('hybridElectricityPricePerKwh')}
            onChange={(value) => {
              const currentValue = formMethods.getValues('hybridElectricityPricePerKwh');
              const finalValue =
                value === 0 && (currentValue === undefined || currentValue === null) ? undefined : value;
              formMethods.setValue('hybridElectricityPricePerKwh', finalValue, { shouldValidate: true });
            }}
            currency={formMethods.watch('currency')}
            error={!!errors.hybridElectricityPricePerKwh}
            helperText={errors.hybridElectricityPricePerKwh ? t(errors.hybridElectricityPricePerKwh.message || '') : ''}
            required={mainsConnection}
            placeholder="0.00"
          />

          <CurrencyNumberInput
            label={t('blueprints:wizard.fields.priceAssumptionsHybrid.co2CostPerTon')}
            value={formMethods.watch('co2CostPerTon')}
            onChange={(value) => formMethods.setValue('co2CostPerTon', value, { shouldValidate: true })}
            currency={formMethods.watch('currency')}
            error={!!errors.co2CostPerTon}
            helperText={errors.co2CostPerTon ? t(errors.co2CostPerTon.message || '') : ''}
            placeholder="0.00"
          />
        </ResponsiveFormContainer>
      </BlueprintStepSection>

      <Divider sx={{ my: 4 }} />

      <PowerProfileStatsDisplay
        peakPower={peakPowerStats}
        averagePower={averagePowerStats}
        energyRequiredWeekly={energyRequiredWeeklyStats}
        projectDurationWeeks={projectDurationWeeks}
      />

      <Box mb={4}>
        <Box sx={{ borderRadius: 2, bgcolor: 'action.hover', pt: 1.5, pb: 1, mb: 0.5 }}>
          <PowerProfileChart
            data={chartData1minInterval}
            title={t('blueprints:wizard.fields.powerProfile.title')}
            titleStyles={{ ml: 2, mb: -1.5 }}
            height={110}
            orientationWarningTitle={t('blueprints:wizard.fields.powerProfile.orientationWarningTitle')}
            orientationWarningMessage={t('blueprints:wizard.fields.powerProfile.orientationWarningMessage')}
          />
        </Box>
        <Typography variant="caption">{t('blueprints:details.powerProfile.disclaimer')}</Typography>
      </Box>

      <Grid container spacing={4} mb={1}>
        <Grid item xs={12} md={6} lg="auto">
          <Typography variant="h3" mb={2}>
            {t('blueprints:wizard.sections.conventionalSetup')}
          </Typography>

          <BlueprintStepSection title={t('blueprints:wizard.fields.gensetRequirements.title')} titleSpacing={20}>
            <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={200}>
              <Autocomplete
                data-testid="genset-kva"
                freeSolo
                size="small"
                options={gensetKvaOptions}
                value={formMethods.watch('gensetKva') || ''}
                getOptionLabel={(option) => String(option)}
                isOptionEqualToValue={(option, value) => option === value}
                onChange={async (_, value) => {
                  const numValue = typeof value === 'number' ? value : parseInt(value || '0') || 0;
                  formMethods.setValue('gensetKva', numValue, { shouldValidate: true, shouldTouch: true });
                  await formMethods.trigger('gensetCount', { shouldFocus: false });
                }}
                onInputChange={async (_, value) => {
                  formMethods.setValue('gensetKva', value, { shouldValidate: true, shouldTouch: true });
                  const gensetCountValue = formMethods.getValues('gensetCount');
                  if (gensetCountValue) {
                    await formMethods.trigger('gensetCount', { shouldFocus: false });
                  }
                }}
                forcePopupIcon
                popupIcon={<Icons.ArrowDropDown />}
                disableClearable={!formMethods.watch('gensetKva')}
                renderInput={(params) => {
                  const isConventionalGensetError =
                    errors.gensetKva?.message ===
                    'blueprints:wizard.validations.conventionalGensetUnderDimensionedError';
                  const shouldShowError = isConventionalGensetError || formMethods.formState.touchedFields.gensetKva;
                  const shouldShowHelperText = shouldShowError && errors.gensetKva && !isConventionalGensetError;
                  return (
                    <TextField
                      {...params}
                      required
                      label={t('blueprints:wizard.fields.gensetRequirements.kvaPlaceholder')}
                      placeholder="kVA"
                      error={shouldShowError && !!errors.gensetKva}
                      helperText={shouldShowHelperText ? t(getValidationError(errors.gensetKva?.message)) : ''}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                    />
                  );
                }}
              />
              <Autocomplete
                data-testid="genset-count"
                freeSolo
                size="small"
                options={gensetCountOptions}
                value={formMethods.watch('gensetCount') || ''}
                getOptionLabel={(option) => String(option)}
                isOptionEqualToValue={(option, value) => option === value}
                onChange={async (_, value) => {
                  const numValue = typeof value === 'number' ? value : parseInt(value || '0') || 0;
                  formMethods.setValue('gensetCount', numValue, { shouldValidate: true, shouldTouch: true });
                  await formMethods.trigger('gensetKva', { shouldFocus: false });
                }}
                onInputChange={async (_, value) => {
                  formMethods.setValue('gensetCount', value, { shouldValidate: true, shouldTouch: true });
                  const gensetKvaValue = formMethods.getValues('gensetKva');
                  if (gensetKvaValue) {
                    await formMethods.trigger('gensetKva', { shouldFocus: false });
                  }
                }}
                forcePopupIcon
                popupIcon={<Icons.ArrowDropDown />}
                disableClearable={!formMethods.watch('gensetCount')}
                renderInput={(params) => {
                  const isConventionalGensetError =
                    errors.gensetCount?.message ===
                    'blueprints:wizard.validations.conventionalGensetUnderDimensionedError';
                  const shouldShowError = isConventionalGensetError || formMethods.formState.touchedFields.gensetCount;
                  const shouldShowHelperText = shouldShowError && errors.gensetCount && !isConventionalGensetError;
                  return (
                    <TextField
                      {...params}
                      required
                      placeholder={t('blueprints:wizard.fields.gensetRequirements.count')}
                      label={t('blueprints:wizard.fields.gensetRequirements.countPlaceholder')}
                      error={shouldShowError && !!errors.gensetCount}
                      helperText={shouldShowHelperText ? t(getValidationError(errors.gensetCount?.message)) : ''}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                    />
                  );
                }}
              />
            </ResponsiveFormContainer>

            <ConventionalGensetValidationAlert
              formData={gensetValidationFormData}
              maxColumnWidth={250}
              minColumnWidth={200}
            />
          </BlueprintStepSection>

          <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={200}>
            <CurrencyNumberInput
              data-testid="conventional-genset-rental-cost-input"
              label={t('blueprints:wizard.fields.priceAssumptionsConventional.gensetRentalCostPerWeek')}
              value={formMethods.watch('conventionalGensetRentalCostPerWeek')}
              onChange={(value) =>
                formMethods.setValue('conventionalGensetRentalCostPerWeek', value, { shouldValidate: true })
              }
              currency={formMethods.watch('currency')}
              error={!!errors.conventionalGensetRentalCostPerWeek}
              helperText={
                errors.conventionalGensetRentalCostPerWeek
                  ? t(errors.conventionalGensetRentalCostPerWeek.message || '')
                  : ''
              }
              required
              placeholder="0.00"
            />
          </ResponsiveFormContainer>

          <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={200} mt={3}>
            <CurrencyNumberInput
              label={t('blueprints:wizard.fields.priceAssumptionsConventional.additionalCostsPerWeek')}
              value={formMethods.watch('conventionalAdditionalCostsPerWeek')}
              onChange={(value) =>
                formMethods.setValue('conventionalAdditionalCostsPerWeek', value, { shouldValidate: true })
              }
              currency={formMethods.watch('currency')}
              error={!!errors.conventionalAdditionalCostsPerWeek}
              helperText={
                errors.conventionalAdditionalCostsPerWeek
                  ? t(errors.conventionalAdditionalCostsPerWeek.message || '')
                  : ''
              }
              placeholder="0.00"
            />
          </ResponsiveFormContainer>
        </Grid>

        <Grid
          item
          xs={12}
          lg="auto"
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'stretch',
          }}
        >
          <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 2 }} />
        </Grid>

        <Grid item xs={12} lg>
          <Typography variant="h3" mb={2}>
            {t('blueprints:wizard.sections.hybridSetup')}
          </Typography>

          <BlueprintStepSection title={t('blueprints:wizard.fields.suggestedRentals.title')} titleSpacing={16}>
            <Typography variant="body2" color="text.secondary">
              {t('blueprints:wizard.fields.suggestedRentals.gensets.title')}
            </Typography>

            <RadioGroup
              row
              value={formMethods.watch('gensetRentalSource')}
              onChange={(e) => {
                const newValue = e.target.value as RentalSource;
                hasManuallySelectedGensetSource.current = true;
                formMethods.setValue('gensetRentalSource', newValue, {
                  shouldValidate: true,
                });
                // Clear all genset fields when switching between catalog and manual
                if (newValue === RentalSource.CATALOG) {
                  // When switching to catalog, clear manual entry fields
                  formMethods.setValue('hybridGensetKva', undefined, { shouldValidate: false });
                  // Reset auto-selection flag to allow auto-selection when switching to catalog
                  hasAutoSelectedGenset.current = false;
                } else {
                  // When switching to manual, clear catalog fields AND manual fields
                  // (to ensure manual fields are not pre-filled with values from catalog/equipment)
                  formMethods.setValue('gensetType', '', { shouldValidate: false });
                  formMethods.setValue('hybridGensetKva', undefined, { shouldValidate: false });
                }
                formMethods.setValue('gensetRentalCount', 1, { shouldValidate: false });
              }}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value={RentalSource.CATALOG}
                control={<Radio />}
                label={t('blueprints:wizard.fields.suggestedRentals.rentalSource.catalog')}
              />
              <FormControlLabel
                value={RentalSource.MANUAL}
                control={<Radio />}
                label={t('blueprints:wizard.fields.suggestedRentals.rentalSource.manual')}
              />
              <FormControlLabel
                value={RentalSource.NONE}
                control={<Radio />}
                label={t('blueprints:wizard.fields.suggestedRentals.rentalSource.noGenset')}
              />
            </RadioGroup>

            <ResponsiveFormContainer
              maxColumnWidth={516}
              sx={{ width: { xs: '100%', md: '50%', lg: 'min(100%, 516px)' } }}
            >
              {gensetRentalSource !== RentalSource.NONE && isGensetRequired && isGensetBelowMinRecommended && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t('blueprints:wizard.fields.suggestedRentals.alert.gensetRecommendation')}
                </Alert>
              )}
              {gensetRentalSource === RentalSource.NONE && (
                <Alert severity="info">{t('blueprints:wizard.fields.suggestedRentals.alert.noGenset')}</Alert>
              )}
            </ResponsiveFormContainer>

            {formMethods.watch('gensetRentalSource') === RentalSource.CATALOG && (
              <Grid container spacing={4}>
                <Grid item xs={12} md={6} lg="auto">
                  <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={150} gap={2}>
                    <Autocomplete
                      data-testid="genset-type-autocomplete"
                      freeSolo
                      size="small"
                      options={getEquipmentOptions(gensetEquipment, EQUIPMENT_TYPES.GENSET, t)}
                      value={getAutocompleteValue(
                        formMethods.watch('gensetType'),
                        gensetEquipment,
                        EQUIPMENT_TYPES.GENSET
                      )}
                      getOptionLabel={(option) => getOptionLabel({ option, isKvaValueShown: true })}
                      isOptionEqualToValue={isOptionEqualToValue}
                      onChange={(_, value) => handleAutocompleteChange(value, formMethods, 'gensetType')}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.id}>
                            {getOptionLabel({ option, isKvaValueShown: true })}
                          </li>
                        );
                      }}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      loading={isEquipmentLoading}
                      disabled={gensetEquipment.length === 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.suggestedRentals.gensets.type')}
                          placeholder={
                            gensetEquipment.length > 0
                              ? t('blueprints:wizard.fields.suggestedRentals.gensets.typePlaceholderCatalog')
                              : t('blueprints:wizard.fields.suggestedRentals.equipment.noEquipmentFound')
                          }
                          error={!!errors.gensetType}
                          helperText={errors.gensetType ? t(errors.gensetType.message || '') : ''}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />

                    <Autocomplete
                      data-testid="genset-count-autocomplete"
                      freeSolo
                      size="small"
                      options={gensetCountOptions}
                      value={formMethods.watch('gensetRentalCount') || 1}
                      getOptionLabel={(option) => String(option)}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(_, value) => {
                        const numValue = typeof value === 'number' ? value : parseInt(String(value) || '0');
                        formMethods.setValue('gensetRentalCount', numValue, { shouldValidate: true });
                      }}
                      onInputChange={(_, value) => {
                        const stringValue = String(value || '');
                        // Check if the entire string is a valid number
                        const isValidNumber = stringValue === '' || /^\d+$/.test(stringValue);
                        const numValue = parseInt(stringValue);
                        // Set 0 for invalid input to trigger validation
                        formMethods.setValue(
                          'gensetRentalCount',
                          stringValue === '' ? 0 : isValidNumber ? numValue : 0,
                          {
                            shouldValidate: true,
                          }
                        );
                      }}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      disableClearable={!formMethods.watch('gensetRentalCount')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.suggestedRentals.gensets.count')}
                          placeholder={t('blueprints:wizard.fields.suggestedRentals.gensets.countPlaceholder')}
                          error={!!errors.gensetRentalCount}
                          helperText={errors.gensetRentalCount ? t(errors.gensetRentalCount.message || '') : ''}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,
                          }}
                        />
                      )}
                    />
                  </ResponsiveFormContainer>

                  <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={516} mt={3}>
                    <CurrencyNumberInput
                      data-testid="genset-rental-cost-input"
                      label={t('blueprints:wizard.fields.suggestedRentals.gensets.rentalCost')}
                      value={formMethods.watch('gensetRentalCost')}
                      onChange={(value) => formMethods.setValue('gensetRentalCost', value, { shouldValidate: true })}
                      currency={formMethods.watch('currency')}
                      error={!!errors.gensetRentalCost}
                      helperText={errors.gensetRentalCost ? t(errors.gensetRentalCost.message || '') : ''}
                      required
                      placeholder="0.00"
                    />
                  </ResponsiveFormContainer>
                </Grid>

                <Grid item xs={12} md={6} lg="auto">
                  {formMethods.watch('gensetType') && (
                    <EquipmentImage equipmentId={formMethods.watch('gensetType')} equipment={gensetEquipment} />
                  )}
                </Grid>
              </Grid>
            )}

            {formMethods.watch('gensetRentalSource') === RentalSource.MANUAL && (
              <Grid container spacing={4}>
                <Grid item xs={12} md={6} lg="auto">
                  <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={150} gap={2}>
                    <Autocomplete
                      data-testid="hybrid-genset-kva"
                      freeSolo
                      size="small"
                      options={hybridGensetKvaOptions}
                      value={formMethods.watch('hybridGensetKva') || ''}
                      getOptionLabel={(option) => String(option)}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={async (_, value) => {
                        const numValue = typeof value === 'number' ? value : parseInt(value || '0') || 0;
                        formMethods.setValue('hybridGensetKva', numValue, { shouldValidate: true, shouldTouch: true });
                        await formMethods.trigger('gensetRentalCount', { shouldFocus: false });
                      }}
                      onInputChange={async (_, value, reason) => {
                        if (reason !== 'input') {
                          return;
                        }
                        formMethods.setValue('hybridGensetKva', value, { shouldValidate: true });
                        const hybridGensetKvaValue = formMethods.getValues('hybridGensetKva');
                        if (hybridGensetKvaValue) {
                          await formMethods.trigger('gensetRentalCount', { shouldFocus: false });
                        }
                      }}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      disableClearable={!formMethods.watch('hybridGensetKva')}
                      renderInput={(params) => {
                        return (
                          <TextField
                            {...params}
                            required
                            label={t('blueprints:wizard.fields.gensetRequirements.kvaPlaceholder')}
                            placeholder="kVA"
                            error={formMethods.formState.touchedFields.hybridGensetKva && !!errors.hybridGensetKva}
                            helperText={
                              errors.hybridGensetKva ? t(getValidationError(errors.hybridGensetKva?.message)) : ''
                            }
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              shrink: true,
                            }}
                          />
                        );
                      }}
                    />

                    <Autocomplete
                      freeSolo
                      size="small"
                      options={gensetCountOptions}
                      value={formMethods.watch('gensetRentalCount') || ''}
                      getOptionLabel={(option) => String(option)}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(_, value) => {
                        const numValue = typeof value === 'number' ? value : parseInt(String(value) || '0');
                        formMethods.setValue('gensetRentalCount', numValue, { shouldValidate: true });
                      }}
                      onInputChange={(_, value) => {
                        const stringValue = String(value || '');
                        // Check if the entire string is a valid number
                        const isValidNumber = stringValue === '' || /^\d+$/.test(stringValue);
                        const numValue = parseInt(stringValue);
                        // Set 0 for invalid input to trigger validation
                        formMethods.setValue(
                          'gensetRentalCount',
                          stringValue === '' ? 0 : isValidNumber ? numValue : 0,
                          {
                            shouldValidate: true,
                          }
                        );
                      }}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      disableClearable={!formMethods.watch('gensetRentalCount')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.suggestedRentals.gensets.count')}
                          placeholder={t('blueprints:wizard.fields.suggestedRentals.gensets.countPlaceholder')}
                          error={!!errors.gensetRentalCount}
                          helperText={errors.gensetRentalCount ? t(errors.gensetRentalCount.message || '') : ''}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,
                          }}
                        />
                      )}
                    />
                  </ResponsiveFormContainer>

                  <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={516} mt={3}>
                    <CurrencyNumberInput
                      data-testid="manual-genset-rental-cost-input"
                      label={t('blueprints:wizard.fields.suggestedRentals.gensets.rentalCost')}
                      value={formMethods.watch('gensetRentalCost')}
                      onChange={(value) => formMethods.setValue('gensetRentalCost', value, { shouldValidate: true })}
                      currency={formMethods.watch('currency')}
                      error={!!errors.gensetRentalCost}
                      helperText={errors.gensetRentalCost ? t(errors.gensetRentalCost.message || '') : ''}
                      required
                      placeholder="0.00"
                    />
                  </ResponsiveFormContainer>
                </Grid>
              </Grid>
            )}

            <EquipmentValidationAlerts
              recommendations={recommendations}
              mode={mode}
              gensetRentalSource={gensetRentalSource}
              batteryRentalSource={batteryRentalSource}
              gensetRentalCount={gensetRentalCount}
              batteryRentalCount={batteryRentalCount}
              gensetKvaManual={gensetKvaManual}
              batteryKwhManual={batteryKwhManual}
              batteryKwManual={batteryKwManual}
              selectedGensetEquipment={selectedGensetEquipment}
              selectedBatteryEquipment={selectedBatteryEquipment}
              type="genset"
              maxColumnWidth={516}
            />

            <Box mt={3}>
              <Typography variant="body2" color="text.secondary">
                {t('blueprints:wizard.fields.suggestedRentals.battery.title')}
              </Typography>
            </Box>

            <RadioGroup
              data-testid="battery-rental-source-radio-group"
              row
              value={formMethods.watch('batteryRentalSource')}
              onChange={(e) => {
                const newValue = e.target.value as RentalSource;
                formMethods.setValue('batteryRentalSource', newValue, {
                  shouldValidate: true,
                });
                // Clear all battery fields when switching between catalog and manual
                if (newValue === RentalSource.CATALOG) {
                  // When switching to catalog, clear manual entry fields
                  formMethods.setValue('batterySize', '', { shouldValidate: false });
                  formMethods.setValue('converterPower', '', { shouldValidate: false });
                  // Reset auto-selection flag to allow auto-selection when switching to catalog
                  hasAutoSelectedBattery.current = false;
                } else {
                  // When switching to manual, clear catalog fields AND manual fields
                  // (to ensure manual fields are not pre-filled with values from catalog/equipment)
                  formMethods.setValue('batteryModel', '', { shouldValidate: false });
                  formMethods.setValue('batterySize', '', { shouldValidate: false });
                  formMethods.setValue('converterPower', '', { shouldValidate: false });
                }
                formMethods.setValue('batteryRentalCount', 1, { shouldValidate: false });
              }}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value={RentalSource.CATALOG}
                control={<Radio />}
                label={t('blueprints:wizard.fields.suggestedRentals.rentalSource.catalog')}
              />
              <FormControlLabel
                value={RentalSource.MANUAL}
                control={<Radio />}
                label={t('blueprints:wizard.fields.suggestedRentals.rentalSource.manual')}
              />
            </RadioGroup>

            {formMethods.watch('batteryRentalSource') === RentalSource.CATALOG && (
              <Grid container spacing={4}>
                <Grid item xs={12} md={6} lg="auto">
                  <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={150} gap={2}>
                    <Autocomplete
                      data-testid="battery-model-autocomplete"
                      freeSolo
                      size="small"
                      options={getEquipmentOptions(batteryEquipment, EQUIPMENT_TYPES.BATTERY, t)}
                      value={getAutocompleteValue(
                        formMethods.watch('batteryModel'),
                        batteryEquipment,
                        EQUIPMENT_TYPES.BATTERY
                      )}
                      getOptionLabel={(option) => getOptionLabel({ option })}
                      isOptionEqualToValue={isOptionEqualToValue}
                      onChange={(_, value) => handleAutocompleteChange(value, formMethods, 'batteryModel')}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      loading={isRentalBatteriesLoading}
                      disabled={batteryEquipment.length === 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.suggestedRentals.battery.model')}
                          placeholder={
                            batteryEquipment.length > 0
                              ? t('blueprints:wizard.fields.suggestedRentals.gensets.typePlaceholderCatalog')
                              : t('blueprints:wizard.fields.suggestedRentals.equipment.noEquipmentFound')
                          }
                          error={!!errors.batteryModel}
                          helperText={errors.batteryModel ? t(errors.batteryModel.message || '') : ''}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />

                    <Autocomplete
                      data-testid="battery-count-autocomplete"
                      freeSolo
                      size="small"
                      options={gensetCountOptions}
                      value={formMethods.watch('batteryRentalCount') || 1}
                      getOptionLabel={(option) => String(option)}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(_, value) => {
                        const numValue = typeof value === 'number' ? value : parseInt(String(value) || '0') || 0;
                        formMethods.setValue('batteryRentalCount', numValue, { shouldValidate: true });
                      }}
                      onInputChange={(_, value) => {
                        const stringValue = String(value || '');
                        // Check if the entire string is a valid number
                        const isValidNumber = stringValue === '' || /^\d+$/.test(stringValue);
                        const numValue = parseInt(stringValue);
                        // Set 0 for invalid input to trigger validation
                        formMethods.setValue(
                          'batteryRentalCount',
                          stringValue === '' ? 0 : isValidNumber ? numValue : 0,
                          {
                            shouldValidate: true,
                          }
                        );
                      }}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      disableClearable={!formMethods.watch('batteryRentalCount')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.suggestedRentals.battery.count')}
                          placeholder={t('blueprints:wizard.fields.suggestedRentals.battery.countPlaceholder')}
                          error={!!errors.batteryRentalCount}
                          helperText={errors.batteryRentalCount ? t(errors.batteryRentalCount.message || '') : ''}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,
                          }}
                        />
                      )}
                    />
                  </ResponsiveFormContainer>

                  <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={516} mt={3}>
                    <CurrencyNumberInput
                      data-testid="battery-rental-cost-input"
                      label={t('blueprints:wizard.fields.suggestedRentals.battery.rentalCost')}
                      value={formMethods.watch('batteryRentalCost')}
                      onChange={(value) => formMethods.setValue('batteryRentalCost', value, { shouldValidate: true })}
                      currency={formMethods.watch('currency')}
                      error={!!errors.batteryRentalCost}
                      helperText={errors.batteryRentalCost ? t(errors.batteryRentalCost.message || '') : ''}
                      required
                      placeholder="0.00"
                    />
                  </ResponsiveFormContainer>
                </Grid>

                <Grid item xs={12} md={6} lg="auto">
                  {formMethods.watch('batteryModel') && (
                    <EquipmentImage equipmentId={formMethods.watch('batteryModel')} equipment={batteryEquipment} />
                  )}
                </Grid>
              </Grid>
            )}

            {formMethods.watch('batteryRentalSource') === RentalSource.MANUAL && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={9}>
                  <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={150} gap={2}>
                    <TextField
                      data-testid="battery-size-input"
                      size="small"
                      type="number"
                      label={t('blueprints:wizard.fields.suggestedRentals.battery.size')}
                      placeholder={t('blueprints:wizard.fields.suggestedRentals.battery.sizePlaceholder')}
                      value={formMethods.watch('batterySize') || ''}
                      onChange={(e) => formMethods.setValue('batterySize', e.target.value, { shouldValidate: true })}
                      error={!!errors.batterySize}
                      helperText={errors.batterySize ? t(errors.batterySize.message || '') : ''}
                      required
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      data-testid="converter-power-input"
                      size="small"
                      type="number"
                      label={t('blueprints:wizard.fields.suggestedRentals.battery.converterPower')}
                      placeholder={t('blueprints:wizard.fields.suggestedRentals.battery.converterPowerPlaceholder')}
                      value={formMethods.watch('converterPower') || ''}
                      onChange={(e) => formMethods.setValue('converterPower', e.target.value, { shouldValidate: true })}
                      error={!!errors.converterPower}
                      helperText={errors.converterPower ? t(errors.converterPower.message || '') : ''}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </ResponsiveFormContainer>

                  <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={150} gap={2} mt={3}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={gensetCountOptions}
                      value={formMethods.watch('batteryRentalCount') || 1}
                      getOptionLabel={(option) => String(option)}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(_, value) => {
                        const numValue = typeof value === 'number' ? value : parseInt(String(value) || '0') || 0;
                        formMethods.setValue('batteryRentalCount', numValue, { shouldValidate: true });
                      }}
                      onInputChange={(_, value) => {
                        const stringValue = String(value || '');
                        // Check if the entire string is a valid number
                        const isValidNumber = stringValue === '' || /^\d+$/.test(stringValue);
                        const numValue = parseInt(stringValue);
                        // Set 0 for invalid input to trigger validation
                        formMethods.setValue(
                          'batteryRentalCount',
                          stringValue === '' ? 0 : isValidNumber ? numValue : 0,
                          {
                            shouldValidate: true,
                          }
                        );
                      }}
                      forcePopupIcon
                      popupIcon={<Icons.ArrowDropDown />}
                      disableClearable={!formMethods.watch('batteryRentalCount')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.suggestedRentals.battery.count')}
                          placeholder={t('blueprints:wizard.fields.suggestedRentals.battery.countPlaceholder')}
                          error={!!errors.batteryRentalCount}
                          helperText={errors.batteryRentalCount ? t(errors.batteryRentalCount.message || '') : ''}
                        />
                      )}
                    />

                    <CurrencyNumberInput
                      label={t('blueprints:wizard.fields.suggestedRentals.battery.rentalCost')}
                      value={formMethods.watch('batteryRentalCost')}
                      onChange={(value) => formMethods.setValue('batteryRentalCost', value, { shouldValidate: true })}
                      currency={formMethods.watch('currency')}
                      error={!!errors.batteryRentalCost}
                      helperText={errors.batteryRentalCost ? t(errors.batteryRentalCost.message || '') : ''}
                      required
                      placeholder="0.00"
                    />
                  </ResponsiveFormContainer>
                </Grid>
              </Grid>
            )}

            <EquipmentValidationAlerts
              recommendations={recommendations}
              mode={mode}
              gensetRentalSource={gensetRentalSource}
              batteryRentalSource={batteryRentalSource}
              gensetRentalCount={gensetRentalCount}
              batteryRentalCount={batteryRentalCount}
              gensetKvaManual={gensetKvaManual}
              batteryKwhManual={batteryKwhManual}
              batteryKwManual={batteryKwManual}
              selectedGensetEquipment={selectedGensetEquipment}
              selectedBatteryEquipment={selectedBatteryEquipment}
              type="battery"
              maxColumnWidth={516}
            />
          </BlueprintStepSection>

          <Grid item container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('blueprints:wizard.fields.priceAssumptions.additionalCosts')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} lg={12}>
              <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={150}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptionsHybrid.additionalCostsPerWeek')}
                  value={formMethods.watch('hybridAdditionalCostsPerWeek')}
                  onChange={(value) =>
                    formMethods.setValue('hybridAdditionalCostsPerWeek', value, { shouldValidate: true })
                  }
                  currency={formMethods.watch('currency')}
                  error={!!errors.hybridAdditionalCostsPerWeek}
                  helperText={
                    errors.hybridAdditionalCostsPerWeek ? t(errors.hybridAdditionalCostsPerWeek.message || '') : ''
                  }
                  placeholder="0.00"
                />
              </ResponsiveFormContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
